import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Smartphone, Store, Bike, Settings, Globe, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const qrConfigs = [
  {
    type: "client",
    label: "App Client",
    description: "Pour les clients qui commandent",
    icon: Smartphone,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50"
  },
  {
    type: "partner",
    label: "App Partenaire",
    description: "Pour les boutiques et commerces",
    icon: Store,
    color: "from-green-500 to-green-600",
    bgColor: "bg-green-50"
  },
  {
    type: "courier",
    label: "App Livreur / Chauffeur",
    description: "Pour les livreurs et chauffeurs",
    icon: Bike,
    color: "from-orange-500 to-orange-600",
    bgColor: "bg-orange-50"
  },
  {
    type: "website",
    label: "Site Web",
    description: "Page d'accueil",
    icon: Globe,
    color: "from-cyan-500 to-cyan-600",
    bgColor: "bg-cyan-50"
  }
];

export default function QRCodesPage() {
  const navigate = useNavigate();

  const downloadQR = (type, label) => {
    const link = document.createElement('a');
    link.href = `${API}/qrcode/${type}`;
    link.download = `ARTEKNOS_${label.replace(/\s/g, '_')}_QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllQR = () => {
    qrConfigs.forEach((config, index) => {
      setTimeout(() => {
        downloadQR(config.type, config.label);
      }, index * 500);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <nav className="bg-white/10 backdrop-blur-lg border-b border-white/10 px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="ARTEKNOS Logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
            />
            <h1 className="text-xl font-bold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>
              QR Codes Applications
            </h1>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={downloadAllQR}
              className="bg-primary hover:bg-primary/90 rounded-full"
              data-testid="download-all-qr-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger tous
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full text-white border-white hover:bg-white hover:text-slate-900"
            >
              Retour
            </Button>
          </div>
        </div>
      </nav>

      <div className="px-4 md:px-8 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
              📱 QR Codes ARTEKNOS
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Scannez le QR code correspondant pour accéder directement à l'application souhaitée.
              Chaque QR code redirige vers l'interface appropriée.
            </p>
          </div>

          {/* QR Codes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qrConfigs.map((config) => {
              const IconComponent = config.icon;
              return (
                <Card
                  key={config.type}
                  className={`${config.bgColor} border-0 shadow-2xl overflow-hidden transform hover:scale-105 transition-all duration-300`}
                  data-testid={`qr-card-${config.type}`}
                >
                  <CardHeader className={`bg-gradient-to-r ${config.color} text-white py-4`}>
                    <CardTitle className="flex items-center gap-3 text-lg">
                      <IconComponent className="w-6 h-6" />
                      {config.label}
                    </CardTitle>
                    <p className="text-sm text-white/80">{config.description}</p>
                  </CardHeader>
                  <CardContent className="p-6 flex flex-col items-center">
                    <div className="bg-white p-4 rounded-2xl shadow-lg mb-4">
                      <img
                        src={`${API}/qrcode/${config.type}`}
                        alt={`QR Code ${config.label}`}
                        className="w-48 h-48"
                        data-testid={`qr-image-${config.type}`}
                      />
                    </div>
                    <Button
                      onClick={() => downloadQR(config.type, config.label)}
                      variant="outline"
                      className="rounded-full w-full"
                      data-testid={`download-qr-${config.type}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Usage Instructions */}
          <Card className="mt-12 bg-white/10 backdrop-blur-lg border-white/20 text-white">
            <CardHeader>
              <CardTitle className="text-xl">📖 Comment utiliser les QR codes</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">1</span>
                </div>
                <h3 className="font-semibold mb-2">Imprimez</h3>
                <p className="text-sm text-slate-300">Téléchargez et imprimez les QR codes pour votre boutique ou flyers</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">2</span>
                </div>
                <h3 className="font-semibold mb-2">Scannez</h3>
                <p className="text-sm text-slate-300">Vos clients/livreurs/chauffeurs scannent avec leur téléphone</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">3</span>
                </div>
                <h3 className="font-semibold mb-2">Connectez</h3>
                <p className="text-sm text-slate-300">Ils accèdent directement à l'application correspondante</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
